import Highcharts from 'highcharts';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import {Component} from 'panel';

import {patchHighchartsForNegativeLog} from 'common/report/util/highcharts';
import {abbreviateNumber, htmlEncodeString} from 'common/util';
import {isIncompleteInterval} from 'common/util/chart';
import * as colors from 'common/util/colors';
import {registerMPElement} from 'common/util/register-element';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import mpFonts from 'common/stylesheets/mixins/fonts.json';

import {DisplayAnalysisType, DisplayPlotStyle} from './enums';
import {IChartDataItem} from './types';

import template from './index.jade';
import css from './index.styl?inline';

patchHighchartsForNegativeLog(Highcharts);

class LineChart extends Component<{}> {
  public formatPointX?: (x: number) => string;
  public formatPointY?: (y: number) => string;
  public formatTicksX?: (min: number, max: number) => number[];
  public formatYAxisLabel?: (value: number) => string;
  public onHighchartMouseOut?: (highchartChart: any) => void;
  public onHighchartMouseOver?: (
    highchartChart: any,
    seriesIdx: number,
    dataIdx: number,
    color: string,
    plotX: number,
    plotY: number,
  ) => void;
  public formatTooltip?: (x: number, y: number, series: any, idx: number) => string;

  public get chartData(): IChartDataItem[] {
    return this._chartData;
  }
  public set chartData(value: IChartDataItem[]) {
    this._chartData = value;
    this.updateChart();
  }

  private chart: any;
  private highcharts: any;
  private _chartData: IChartDataItem[];

  public static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'chart-height': {type: `number`, default: 400, description: `height of the chart in px`},
      'chart-width': {type: `number`, default: 800, description: `width of the chart in px`},
      'color-theme': {
        type: `string`,
        default: `heterogenous`,
        description: `color theme of the lines plotted on the chart`,
      },
      'display-analysis-type': {
        type: `string`,
        default: DisplayAnalysisType.Linear,
        enum: [DisplayAnalysisType.Linear, DisplayAnalysisType.Logarithmic],
      },
      'display-plot-style': {
        type: `string`,
        default: DisplayPlotStyle.Standard,
        enum: [DisplayPlotStyle.Stacked, DisplayPlotStyle.Standard],
        description: `plot style, standard vs stacked`,
      },
      'is-time-series': {
        type: `boolean`,
        default: true,
        description: `display a datetime x axis or display it how it is`,
      },
      'show-x-axis-ticks': {type: `boolean`, default: true, description: `show x axis values below the chart`},
      'x-axis-plot-bands': {
        type: `json`,
        description: `modify plot bands, for example changing background color across certain x axes vertically`,
      },
      'x-axis-plot-lines': {type: `json`, description: `add plot lines going vertical`},
      'show-y-axis-ticks': {
        type: `boolean`,
        default: true,
        description: `show y axis values to the right of the chart`,
      },
      'time-unit': {
        type: `string`,
        default: null,
        enum: [TimeUnit.Day, TimeUnit.Month, TimeUnit.Week],
        description: `unit to align, which is only necessary if you want incomplete interval zoning`,
      },
      'utc-offset': {
        type: `string`,
        description: `utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'incomplete-to-date': {
        type: `boolean`,
        default: false,
        description: `zone with to date, overrides incomplete interval check, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'incomplete-from-date': {
        type: `boolean`,
        default: false,
        description: `zone with from date as well, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
    };
  }

  public get config() {
    return {
      css,
      useShadowDom: true,
      template,
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    if (!this.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    const chartEl = document.createElement(`div`);
    this.el.appendChild(chartEl);
    this.highcharts = new Highcharts.Chart(chartEl, this.getChartOptions());
    this.el.addEventListener(`interestingEventClicked`, (ev) => {
      this.dispatchEvent(new CustomEvent(`interestingEventClicked`, {detail: ev[`detail`]}));
    });
    this.updateChart();
  }

  public disconnectedCallback() {
    if (this.highcharts) {
      this.highcharts.destroy();
      delete this.highcharts;
    }

    if (this.chart) {
      this.chart.remove();
      delete this.chart;
    }

    super.disconnectedCallback();
  }

  reflowChart() {
    window.requestAnimationFrame(() => {
      this.highcharts.reflow();
    });
  }

  private updateChart() {
    if (this.highcharts) {
      this.highcharts.update(
        this.getChartOptions(),
        true, // redraw after update
        true, // oneToOne: add/remove series when they change
      );
      // expand chart to full area width even if chart is fully loaded but we don't know the width size
      this.reflowChart();
    }
  }

  private formatSeries() {
    return this.chartData
      .map((dataSegment, segmentIdx) => {
        const colorIdx = segmentIdx % this.chartData.length;
        return {
          name: dataSegment.name,
          data: dataSegment.data,
          interestingPoints: dataSegment.interestingPoints,
          type: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? `area` : `line`,
          color: dataSegment.color || colors.getChartSegmentColorCombo(colorIdx, this.attr(`color-theme`)).primary,
          visible: dataSegment.visible,
          dashStyle: dataSegment.dashStyle || `Solid`,
        };
      })
      .map((lineSeries) => {
        lineSeries.data = sortBy(lineSeries.data, (d) => d[0]);
        const timeUnit = this.attr(`time-unit`);
        let incompletePathsAndZoning = {};
        const dataLength = lineSeries.data.length;
        const showIncompleteToDate = this.attr(`incomplete-to-date`);
        const showIncompleteFromDate = this.attr(`incomplete-from-date`);

        if (timeUnit) {
          const isIncompletePath = isIncompleteInterval(lineSeries.data, {
            unit: timeUnit,
            utcOffset: this.attr(`utc-offset`),
          });
          const showIncomplete = isIncompletePath && lineSeries.data.length >= 2;
          incompletePathsAndZoning = {
            isIncompletePath,
            ...(showIncomplete && {
              zoneAxis: `x`,
              zones: [{value: lineSeries.data[dataLength - 2][0]}, {dashStyle: `ShortDot`}],
            }),
          };
        } else if (showIncompleteFromDate || showIncompleteToDate) {
          const zones = [];
          // Check for length of 4 since length of 3 or 2 is fine being ShortDot
          // if Solid dashstyle not added, the whole line becomes ShortDot
          if (showIncompleteFromDate) {
            zones.push({value: lineSeries.data[1][0], dashStyle: `ShortDot`});
            if (dataLength >= 4) {
              zones.push({value: lineSeries.data[2][0], dashStyle: `Solid`});
            }
          }
          if (showIncompleteToDate) {
            if (!showIncompleteFromDate || dataLength >= 4) {
              zones.push({value: lineSeries.data[dataLength - 2][0], dashStyle: `Solid`});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], dashStyle: `ShortDot`});
          }
          incompletePathsAndZoning = {
            isIncompletePath: true,
            zoneAxis: `x`,
            zones,
          };
        }
        return {
          ...lineSeries,
          ...incompletePathsAndZoning,
          formatPointX: this.formatPointX,
          formatPointY: this.formatPointY,
          formatTicksX: this.formatTicksX,
          onHighchartMouseOut: this.onHighchartMouseOut,
          onHighchartMouseOver: this.onHighchartMouseOver,
          formatTooltip: this.formatTooltip,
        };
      })
      .map((dataSegment, segmentIdx) => {
        const {data, interestingPoints} = dataSegment;
        if (interestingPoints && interestingPoints.size > 0) {
          const colorIdx = (segmentIdx % this.chartData.length) + 1;
          const interestingColor = colors.getChartSegmentColorCombo(colorIdx, this.attr(`color-theme`)).primary;
          const newData: any[] = data.map(([x, y]) => {
            if (interestingPoints.has(x)) {
              return {
                cursor: `pointer`,
                marker: {
                  enabled: true,
                  symbol: `circle`,
                  fillColor: interestingColor,
                  radius: 6,
                  states: {
                    hover: {
                      fillColor: interestingColor,
                      radius: 8,
                    },
                  },
                },
                x,
                y,
              };
            } else {
              return [x, y];
            }
          });
          dataSegment.data = newData;
        }
        return dataSegment;
      });
  }

  // TODO: Highcharts 7 is typed in beta, but we are using Highcharts 6. Use Highcharts 7's typing system
  // when we eventually switch over
  private getChartOptions(): any {
    const highchartChart = this.highcharts;
    const series = this.formatSeries();

    return {
      chart: {
        backgroundColor: null,
        height: `${this.attr(`chart-height`)}px`,
        marginLeft: this.attr(`show-y-axis-ticks`) ? 50 : 10,
        marginTop: 0,
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
          fontSize: `12px,`,
        },
        type: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? `area` : `line`,
        width: this.attr(`chart-width`),
        zoomType: false,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      loading: {
        labelStyle: {
          color: `transparent`,
        },
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          shadow: false,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
        series: {
          animation: false,
          fillOpacity: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? 0.2 : 1,
          marker: {
            enabled: false,
            hover: {
              enabled: true,
            },
            lineWidth: 2,
            symbol: `circle`,
          },
          point: {
            events: {
              mouseOver() {
                const {color, index: dataIdx, plotX, plotY, series: chartSeries} = this;
                const {onHighchartMouseOver, index: seriesIdx} = chartSeries.userOptions;
                return onHighchartMouseOver
                  ? onHighchartMouseOver(highchartChart, seriesIdx, dataIdx, color, plotX, plotY)
                  : undefined;
              },
            },
          },
          events: {
            click(ev) {
              const {interestingPoints} = ev.point.series.userOptions;
              const {x, y} = ev.point.options;
              if (!interestingPoints || !interestingPoints.has(x)) {
                return;
              }
              const {clientX, clientY} = ev;
              ev.point.series.chart.container.dispatchEvent(
                new CustomEvent(`interestingEventClicked`, {
                  bubbles: true,
                  detail: {x, y, clientX, clientY},
                }),
              );
            },
            mouseOut() {
              const {onHighchartMouseOut} = this.userOptions;
              return onHighchartMouseOut ? onHighchartMouseOut(highchartChart) : undefined;
            },
          },
          shadow: false,
          ...(this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked && {
            stacking: `normal`,
          }),
        },
      },
      series,
      time: {
        useUTC: false,
      },
      tooltip: {
        backgroundColor: null,
        borderWidth: 0,
        formatter() {
          // Highcharts callbacks don't work with arrow functions
          const {color, index, name, options} = this.series;

          // Override style to make the tooltip not look like the default
          if (options.formatTooltip) {
            return options.formatTooltip(this.x, this.y, this.series, index);
          }

          const formattedLeftLabel = options.formatPointX
            ? options.formatPointX(this.x)
            : Highcharts.dateFormat(`%b %e, %Y`, this.x);
          const formattedValue = options.formatPointY ? options.formatPointY(this.y) : this.y.toLocaleString();
          const seriesLabel = name;

          const defaultLabelFormatter = `
            <div class="row">
              <span class="value" style="font-weight: 700">${htmlEncodeString(formattedLeftLabel)}:</span>
              <span class="value">${formattedValue}</span>
            </div>
          `;
          return `
            <div class="line-chart-tooltip">
              <div class="title" style="background-color: ${color};">${seriesLabel}</div>
              <div class="result">
                ${defaultLabelFormatter}
              </div>
            </div>
          `;
        },
        padding: 0,
        shadow: false,
        shape: `square`,
        useHTML: true,
      },
      title: {
        text: null,
      },
      xAxis: {
        endOnTick: false,
        formatTicksX: this.formatTicksX,
        labels: {
          enabled: this.attr(`show-x-axis-ticks`),
        },
        lineColor: colors.mpColors[`$gray2`],
        lineWidth: 1,
        marginBottom: 10,
        maxPadding: 0.01,
        minPadding: 0.01,
        ...(this.attr(`x-axis-plot-bands`) && {
          plotBands: [pick(this.attr(`x-axis-plot-bands`), [`color`, `from`, `to`])],
        }),
        ...(this.attr(`x-axis-plot-lines`) && {
          plotLines: [pick(this.attr(`x-axis-plot-lines`), [`color`, `value`, `width`, `zIndex`])],
        }),
        tickPosition: `outside`,
        tickPositioner() {
          const {dataMax, dataMin, options} = this;
          const {formatTicksX} = options;
          return formatTicksX ? formatTicksX(dataMin, dataMax) : undefined;
        },
        tickmarkPlacement: `on`,
        title: {
          text: null,
        },
        type: this.attr(`is-time-series`) ? `datetime` : `linear`,
      },
      yAxis: {
        allowDecimals: true,
        endOnTick: false,
        gridLineColor: colors.mpColors[`$gray2`],
        gridLineDashStyle: `shortDash`,
        formatYAxisLabel: this.formatYAxisLabel,
        labels: {
          enabled: this.attr(`show-y-axis-ticks`),
          formatter() {
            const {userOptions} = this.axis;
            return userOptions.formatYAxisLabel
              ? userOptions.formatYAxisLabel(this.value)
              : abbreviateNumber(this.value);
          },
        },
        lineWidth: 1,
        startOnTick: true,
        title: {
          text: null,
        },
        type:
          this.attr(`display-analysis-type`) === DisplayAnalysisType.Logarithmic
            ? DisplayAnalysisType.Logarithmic
            : DisplayAnalysisType.Linear,
      },
    };
  }
}

export default registerMPElement(`mp-line-chart`, LineChart);
